// Full-attention popup that narrates a credential hand-out for a
// non-technical operator. The fob/card flow walks through
// "Waiting for keyfob scan" → "Adding…" → "All set!"; PIN and Touch Pass
// reuse the same success/working/error states without the scan stage.
//
// Kept deliberately presentational: the parent owns the state machine,
// the polling, and the countdown. This component just renders the phase
// it is handed and routes the buttons back out. That keeps it trivial to
// unit-test and impossible to leave a reader "listening" by accident.

import { useEffect, useRef } from 'react';
import { Button, Icon, Spinner } from '@ng-unifi/ui';

export type EnrollmentPhase = 'waiting' | 'issuing' | 'success' | 'expired' | 'error';

export interface EnrollmentFeedbackProps {
  open: boolean;
  phase: EnrollmentPhase;
  /** Friendly name of the reader the fob is presented to. */
  readerName?: string | null;
  /** Person the key is being issued to (for the success line). */
  personName?: string | null;
  /** What the new key is called, e.g. "Fob" / "PIN". */
  credentialLabel?: string | null;
  /** Seconds left in the scan window — drives the live countdown. */
  secondsLeft?: number | null;
  /** Human-readable failure text for the error phase. */
  errorMessage?: string | null;
  /**
   * "scan" = fob/card (has the waiting stage). "instant" = PIN / Touch Pass
   * (no scan; opens straight into "Adding…").
   */
  variant?: 'scan' | 'instant';
  /** Stop a scan that's still listening (frees the reader). */
  onCancel: () => void;
  /** Start over after a timeout or error. */
  onRetry: () => void;
  /** Dismiss the popup once finished (or after an error). */
  onClose: () => void;
}

function formatCountdown(totalSeconds: number): string {
  const safe = Math.max(0, Math.floor(totalSeconds));
  const minutes = Math.floor(safe / 60);
  const seconds = safe % 60;
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
}

export function EnrollmentFeedback({
  open,
  phase,
  readerName,
  personName,
  credentialLabel,
  secondsLeft,
  errorMessage,
  variant = 'scan',
  onCancel,
  onRetry,
  onClose,
}: EnrollmentFeedbackProps) {
  const panelRef = useRef<HTMLDivElement | null>(null);

  // Move focus into the popup when it opens so screen readers announce it
  // and keyboard focus isn't stranded behind the backdrop.
  useEffect(() => {
    if (open) panelRef.current?.focus();
  }, [open]);

  // Escape backs out the same way the on-screen buttons do — but never
  // while the credential is mid-save (that would orphan the request).
  useEffect(() => {
    if (!open) return undefined;
    function onKey(e: KeyboardEvent) {
      if (e.key !== 'Escape') return;
      dismiss();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, phase]);

  if (!open) return null;

  function dismiss() {
    if (phase === 'issuing') return; // don't abandon an in-flight save
    if (phase === 'waiting') onCancel();
    else onClose();
  }

  const label = credentialLabel?.trim() || 'key';
  const person = personName?.trim();
  const reader = readerName?.trim();

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="enrollment-feedback-title"
      onClick={(e) => {
        if (e.target === e.currentTarget) dismiss();
      }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-on-surface/40 p-margin-mobile"
    >
      <div
        ref={panelRef}
        tabIndex={-1}
        className="w-full max-w-[420px] max-h-[calc(100dvh-2rem)] overflow-y-auto rounded-2xl border border-outline-variant bg-surface-container-lowest p-6 text-center shadow-xl outline-none sm:p-8"
      >
        {/* Live region: announces each phase change to assistive tech. */}
        <p className="sr-only" aria-live="polite">
          {phase === 'waiting'
            ? 'Waiting for a key fob scan'
            : phase === 'issuing'
              ? 'Adding the key'
              : phase === 'success'
                ? 'Success, the key is now active'
                : phase === 'expired'
                  ? 'The scan timed out'
                  : 'Something went wrong'}
        </p>

        {phase === 'waiting' ? (
          <Stage
            visual={<RadarVisual />}
            title="Waiting for keyfob scan"
            body={
              reader
                ? `Hold the key fob or card flat against the ${reader} reader.`
                : 'Hold the key fob or card flat against the reader.'
            }
          >
            <div className="mt-5 flex items-center justify-center gap-2 font-body-md text-body-md text-on-surface-variant">
              <span className="inline-flex h-2 w-2 rounded-full bg-success motion-safe:animate-pulse" />
              <span>
                Listening
                {typeof secondsLeft === 'number' && secondsLeft > 0
                  ? ` · ${formatCountdown(secondsLeft)} left`
                  : ''}
              </span>
            </div>
            <div className="mt-6">
              <Button type="button" variant="secondary" onClick={onCancel} className="w-full">
                Cancel
              </Button>
            </div>
          </Stage>
        ) : null}

        {phase === 'issuing' ? (
          <Stage
            visual={
              <div className="mx-auto flex h-24 w-24 items-center justify-center rounded-full bg-surface-container">
                <Spinner className="h-10 w-10 text-on-surface" />
              </div>
            }
            title={variant === 'scan' ? 'Adding the fob…' : 'Adding the key…'}
            body="Hang tight — this only takes a moment."
          />
        ) : null}

        {phase === 'success' ? (
          <Stage
            visual={
              <div className="mx-auto flex h-24 w-24 items-center justify-center rounded-full bg-success/10 motion-safe:animate-enroll-pop">
                <Icon name="check_circle" fill className="text-6xl text-success" />
              </div>
            }
            title="All set!"
            body={
              person
                ? `The ${label} is now active for ${person}.`
                : `The ${label} is now active.`
            }
          >
            <div className="mt-6">
              <Button type="button" onClick={onClose} className="w-full">
                Done
              </Button>
            </div>
          </Stage>
        ) : null}

        {phase === 'expired' ? (
          <Stage
            visual={
              <div className="mx-auto flex h-24 w-24 items-center justify-center rounded-full bg-warning/10">
                <Icon name="schedule" className="text-5xl text-warning" />
              </div>
            }
            title="Scan timed out"
            body="We didn't detect a fob in time. Want to try again?"
          >
            <div className="mt-6 flex flex-col gap-2 sm:flex-row-reverse">
              <Button type="button" onClick={onRetry} className="w-full sm:flex-1">
                Try again
              </Button>
              <Button
                type="button"
                variant="ghost"
                onClick={onClose}
                className="w-full sm:flex-1"
              >
                Close
              </Button>
            </div>
          </Stage>
        ) : null}

        {phase === 'error' ? (
          <Stage
            visual={
              <div className="mx-auto flex h-24 w-24 items-center justify-center rounded-full bg-error-container">
                <Icon name="error" className="text-5xl text-on-error-container" />
              </div>
            }
            title="Something went wrong"
            body={errorMessage?.trim() || 'We couldn’t finish adding the key. Please try again.'}
          >
            <div className="mt-6 flex flex-col gap-2 sm:flex-row-reverse">
              <Button type="button" onClick={onRetry} className="w-full sm:flex-1">
                Try again
              </Button>
              <Button
                type="button"
                variant="ghost"
                onClick={onClose}
                className="w-full sm:flex-1"
              >
                Close
              </Button>
            </div>
          </Stage>
        ) : null}
      </div>
    </div>
  );
}

function Stage({
  visual,
  title,
  body,
  children,
}: {
  visual: React.ReactNode;
  title: string;
  body: string;
  children?: React.ReactNode;
}) {
  return (
    <div>
      {visual}
      <h2
        id="enrollment-feedback-title"
        className="mt-6 font-headline-sm text-headline-sm text-on-surface"
      >
        {title}
      </h2>
      <p className="mt-2 font-body-md text-body-md text-on-surface-variant">{body}</p>
      {children}
    </div>
  );
}

/** Concentric "listening" rings around a contactless icon. */
function RadarVisual() {
  return (
    <div className="relative mx-auto flex h-28 w-28 items-center justify-center">
      <span className="absolute inline-flex h-24 w-24 rounded-full bg-primary/10 motion-safe:animate-ping" />
      <span className="absolute inline-flex h-16 w-16 rounded-full bg-primary/10 motion-safe:animate-ping [animation-delay:500ms]" />
      <span className="relative inline-flex h-20 w-20 items-center justify-center rounded-full bg-primary text-on-primary">
        <Icon name="contactless" className="text-4xl" />
      </span>
    </div>
  );
}
